/**
 * ENG-10869 — where `.claude.json` lives, in ONE place.
 *
 * ## The bug this exists to close
 *
 * `~/.claude.json` sits OUTSIDE `~/.claude`, so the container spawn path had to
 * give it its own mount — and the only available shape was a **single-file**
 * bind. A file bind pins the inode at container start, and Claude Code replaces
 * that file by **atomic rename**. So every host-side write orphaned the view of
 * every already-running container, permanently, until it respawned.
 *
 * Measured on `agt-aws-1` 2026-09-22 00:37Z: SIX live versions of one config
 * across eight agents, the oldest 12+ hours stale. Containers that booted
 * together shared an inode; containers that booted apart did not.
 *
 * Two consequences, both observed in prod:
 *
 *  - **Stale identity.** `.credentials.json` is inside the `~/.claude`
 *    DIRECTORY mount and propagates correctly, so a refreshed token reaches the
 *    container while `oauthAccount` in `.claude.json` does not. Current
 *    credentials, stale account, and Claude Code shows the login picker
 *    (alyve-host-1, 2026-09-21, ~9h50m across four agents).
 *  - **Corruption.** A container CANNOT rename onto its own file bind — it
 *    fails EBUSY (verified on agt-runtime; the same finding ENG-8854 recorded
 *    for `history.jsonl` on 2026-09-11) — so a container write falls back to
 *    truncate-in-place. Agents that booted together share one inode and tear
 *    each other's writes: "Unexpected EOF", and the 0-byte case at its limit
 *    (ENG-10140).
 *
 * Fleet evidence that the mount and not the concurrency is the cause: 39
 * corruptions across 18 containerised hosts / 61 agents, and ZERO across 5
 * non-containerised hosts / 17 agents — one of which runs seven agents against
 * that same path.
 *
 * ## The fix, and why it is this one
 *
 * `CLAUDE_CONFIG_DIR` relocates the whole config root, `.claude.json` included.
 * Verified on the `agt-runtime` image: with it set, Claude Code wrote
 * `.claude.json`, `backups/`, `projects/` and `sessions/` inside that directory,
 * and authenticated from a `.credentials.json` placed there.
 *
 * Pointing it at `~/.claude` moves `.claude.json` INSIDE the directory mount
 * that already works, and moves nothing else — `settings.json`, `plugins`,
 * `backups/` and `projects/` are already there. Both directions were then
 * verified from a production host with no code changed:
 *
 *     container renames a file inside a DIR mount  -> SUCCEEDED (213400 -> 213401)
 *     host renames it; running container re-reads  -> sees the new content, no respawn
 *
 * ## Why this module exists rather than a constant
 *
 * FIVE call sites computed this path independently — the spawn mount, the pair
 * flow, the device-state probe, the account fingerprint and the config-health
 * check. That is the same drift hazard {@link CLAUDE_MANAGED_SETTINGS_DIR} was
 * made a single source of truth for, and it is worse here: if the host writes
 * one path while containers read another, pairing silently stops reaching
 * agents. Every consumer resolves through here so they cannot disagree.
 */

import { constants, copyFileSync, existsSync, mkdirSync, renameSync, unlinkSync } from 'node:fs';
/**
 * Path resolution is DELEGATED to core, not reimplemented here.
 *
 * `ensureWorkspaceTrusted` in core writes this file on the manager's tick, so
 * core needs the rule too. Two copies of it would drift, and the symptom would
 * be a write that succeeds into a file nothing reads. This module owns the
 * MIGRATION (seeding, readiness); core owns the PATH.
 */
export {
  CLAUDE_CONFIG_DIR_ENV,
  claudeConfigDirFor as claudeConfigDir,
  claudeJsonPathFor as claudeJsonPath,
  legacyClaudeJsonPathFor as legacyClaudeJsonPath,
  effectiveClaudeJsonPath,
  inspectClaudeConfig,
  type ClaudeConfigState,
} from '@augmented/core/provisioning/frameworks/claudecode/config-location.js';
import {
  claudeConfigDirFor as claudeConfigDir,
  claudeJsonPathFor as claudeJsonPath,
  legacyClaudeJsonPathFor as legacyClaudeJsonPath,
  claudeConfigMtimeMs,
  inspectClaudeConfig,
} from '@augmented/core/provisioning/frameworks/claudecode/config-location.js';

export type SeedOutcome =
  /** The config dir already holds a usable `.claude.json`; nothing to do. */
  | 'already-present'
  /** Copied from the legacy path. */
  | 'seeded'
  /**
   * The legacy file was NEWER and usable, so it was copied forward over an
   * existing target. This is the `claude /login` recovery path — see the
   * docblock on {@link seedClaudeConfigFromLegacy}.
   */
  | 'reseeded-from-newer-legacy'
  /** Neither location has one — a never-paired host. Claude Code will create it. */
  | 'absent'
  /** The legacy file exists but is not usable; deliberately NOT copied. */
  | 'legacy-unusable'
  /** The copy itself failed. */
  | 'failed';

/**
 * Move the host onto the new location, once.
 *
 * **Ordering is the whole risk of this change.** If a container is handed
 * `CLAUDE_CONFIG_DIR` before the file exists there, Claude Code creates a fresh
 * one: credentials still resolve (they are already in the config dir) but
 * `oauthAccount` and `hasCompletedOnboarding` are gone, which is the login
 * picker — the exact outage this change exists to prevent, caused by the fix.
 * So this MUST run before the first spawn that uses the new path, and the
 * caller must treat a non-seed as a reason not to proceed.
 *
 * The copy is atomic by temp-then-rename INSIDE the destination directory, so a
 * crash mid-write cannot leave a truncated config where agents will read one —
 * the same property `.mcp.json` got in ENG-4787 and `.claude.json` never had.
 *
 * A legacy file that does not parse is NOT copied. Propagating a corrupt config
 * to the new location would migrate the incident along with the data, and the
 * corrupt original is exactly what the old mount was producing.
 */
export function seedClaudeConfigFromLegacy(opts: {
  homeDir?: string;
  env?: NodeJS.ProcessEnv;
  log?: (msg: string) => void;
} = {}): SeedOutcome {
  const env = opts.env ?? process.env;
  const log = opts.log ?? (() => {});
  const target = claudeJsonPath(opts.homeDir, env);
  const legacy = legacyClaudeJsonPath(opts.homeDir, env);

  if (target === legacy) return 'already-present';

  try {
    const targetState = inspectClaudeConfig(target);
    const legacyState = inspectClaudeConfig(legacy);

    // ENG-10869: NOT `existsSync(target)`.
    //
    // The one-shot existence test was wrong in both directions. A file that
    // merely exists can be account-less — `ensureWorkspaceTrusted` creates
    // exactly such a file — so the seed would skip, the spawn would drop the
    // legacy bind on a config no agent can start from, and every agent on the
    // host would sit on the login picker. And once the target existed, a LATER
    // write to the legacy path could never be picked up, which silently broke
    // the documented recovery: `agt host pair` and the manager's own
    // blocked-agent message both tell an operator to run `claude /login`, and a
    // plain shell has no CLAUDE_CONFIG_DIR, so that writes the LEGACY file. The
    // seed said `already-present`, the fresh account never reached an agent,
    // and the archive step then renamed the operator's new config aside — once
    // per re-login, with Claude Code reporting a successful login each time.
    if (targetState === 'symlink') {
      log(
        `[claude-config] ${target} is a SYMLINK — refusing to seed through it. ` +
          `Remove it on the host; a config that an agent can replace with a link is not one we write to.`,
      );
      return 'failed';
    }

    const legacyUsable = legacyState === 'usable';
    const targetUsable = targetState === 'usable';
    // A legacy file written AFTER the target is the recovery case above.
    const legacyIsNewer = claudeConfigMtimeMs(legacy) > claudeConfigMtimeMs(target);

    if (targetUsable && !(legacyUsable && legacyIsNewer)) return 'already-present';

    if (legacyState === 'missing') {
      log(`[claude-config] no legacy ${legacy} to seed from — Claude Code will create ${target}`);
      return 'absent';
    }
    if (!legacyUsable) {
      log(
        `[claude-config] legacy ${legacy} is ${legacyState} — NOT seeding. ` +
          `Re-pair the host, then the next spawn seeds a good file.`,
      );
      return 'legacy-unusable';
    }
    const reseeding = targetUsable;
    if (reseeding) {
      log(
        `[claude-config] legacy ${legacy} is NEWER than ${target} and carries an account — ` +
          `copying it forward (an operator ran 'claude /login' outside the config dir)`,
      );
    }

    mkdirSync(claudeConfigDir(opts.homeDir, env), { recursive: true });
    const tmp = `${target}.seed-${process.pid}`;
    try {
      // ENG-10869 security: COPYFILE_EXCL, for the same reason the trust
      // writer uses `wx`. This destination sits in a directory the agent can
      // write, and the manager runs outside the container — a pre-planted
      // symlink here would have the manager copy the config through it onto an
      // arbitrary host file. EXCL fails with EEXIST rather than following.
      copyFileSync(legacy, tmp, constants.COPYFILE_EXCL);
      renameSync(tmp, target);
    } catch (err) {
      try {
        if (existsSync(tmp)) unlinkSync(tmp);
      } catch {
        // Best effort; a stray .seed-<pid> is inert.
      }
      throw err;
    }
    log(`[claude-config] seeded ${target} from ${legacy}`);
    return reseeding ? 'reseeded-from-newer-legacy' : 'seeded';
  } catch (err) {
    log(`[claude-config] seeding ${target} failed: ${(err as Error).message}`);
    return 'failed';
  }
}

/**
 * True when the config dir holds a usable `.claude.json` — i.e. it is safe to
 * spawn against the new location.
 *
 * Deliberately more than a stat, and more than a parse. The failures this gates
 * are an agent starting against a config that is truncated (indistinguishable
 * from a healthy one by size or mtime) AND one that parses but carries no
 * account — the shape `ensureWorkspaceTrusted` creates, which would otherwise
 * let this fix's own writer manufacture a config that looks ready and sends
 * every agent on the host to the login picker. See {@link inspectClaudeConfig}.
 */
export function claudeConfigDirIsReady(
  homeDir?: string,
  env: NodeJS.ProcessEnv = process.env,
): boolean {
  return inspectClaudeConfig(claudeJsonPath(homeDir, env)) === 'usable';
}

export type ArchiveOutcome =
  /** Legacy file moved aside; a rolled-back manager will now fail LOUDLY. */
  | 'archived'
  /** Nothing at the legacy path — already migrated, or never paired. */
  | 'nothing-to-archive'
  /** Refused: the new location is not proven good, so the legacy file is the only copy. */
  | 'refused-target-not-ready'
  /**
   * Refused: the legacy file is NEWER than the target, so it is somebody's
   * fresh `claude /login` and archiving it would throw away the recovery.
   */
  | 'refused-legacy-newer'
  | 'failed';

/**
 * ENG-10869 — make a CLI rollback fail loudly instead of quietly serving a
 * stale account.
 *
 * ## The hazard this closes
 *
 * After the migration, every write goes to `<configDir>/.claude.json` and the
 * legacy `<home>/.claude.json` is frozen at whatever it held on migration day.
 * Nothing syncs it. So a manager rolled back to a build without
 * `CLAUDE_CONFIG_DIR` would bind that stale file into every container and the
 * fleet would come up on a *plausible* config — the right shape, the right
 * keys, and an `oauthAccount` that may be several re-pairings out of date.
 *
 * That is a false-green: agents start, health reads fine, and they are
 * authenticated as the wrong account or not at all. Moving the file aside
 * converts it into `CLAUDE LOGIN REQUIRED`, which the manager already detects,
 * logs by name and surfaces — an outage that says what it is, and is one `mv`
 * from being undone.
 *
 * ## Why it refuses unless the target is ready
 *
 * Until the seed has landed and parsed, the legacy file is the ONLY copy of the
 * host's identity. Archiving first and seeding second would be a window in
 * which a crash loses the account outright.
 *
 * ## Why it renames rather than deletes
 *
 * The archived name is dated and predictable, so recovery is a rename and the
 * log line says so. Deleting would make the rollback path unrecoverable, which
 * is a worse trade than the one being fixed.
 */
export function archiveLegacyClaudeJson(opts: {
  homeDir?: string;
  env?: NodeJS.ProcessEnv;
  log?: (msg: string) => void;
  now?: () => Date;
} = {}): ArchiveOutcome {
  const env = opts.env ?? process.env;
  const log = opts.log ?? (() => {});
  const legacy = legacyClaudeJsonPath(opts.homeDir, env);
  const target = claudeJsonPath(opts.homeDir, env);

  if (target === legacy) return 'nothing-to-archive';

  try {
    if (!existsSync(legacy)) return 'nothing-to-archive';
    if (!claudeConfigDirIsReady(opts.homeDir, env)) {
      // The legacy file is the only usable copy. Leave it exactly where a
      // rolled-back manager, and the current one, can still find it.
      log(
        `[claude-config] NOT archiving ${legacy} — ${target} is absent or does not parse, ` +
          `so the legacy file is still the only usable config`,
      );
      return 'refused-target-not-ready';
    }
    // ENG-10869: a legacy file NEWER than the target is an operator's fresh
    // `claude /login`, not a stale leftover. The seed above copies it forward;
    // if that has not happened yet, archiving here would rename the only copy
    // of a just-created account aside — repeatedly, once per re-login, while
    // Claude Code reported each login as successful.
    if (claudeConfigMtimeMs(legacy) > claudeConfigMtimeMs(target)) {
      log(
        `[claude-config] NOT archiving ${legacy} — it is NEWER than ${target}, so it is a fresh login ` +
          `that has not been copied forward yet`,
      );
      return 'refused-legacy-newer';
    }

    const stamp = (opts.now?.() ?? new Date()).toISOString().replace(/[:.]/g, '-');
    const archived = `${legacy}.pre-eng10869-${stamp}`;
    renameSync(legacy, archived);
    log(
      `[claude-config] archived ${legacy} -> ${archived}. ` +
        `Config now lives at ${target}. To undo a CLI rollback: mv '${archived}' '${legacy}'`,
    );
    return 'archived';
  } catch (err) {
    log(`[claude-config] archiving ${legacy} failed: ${(err as Error).message}`);
    return 'failed';
  }
}

